// Canvas of a meeting being recorded or saved (BUILD 1 WP-4; slot props in
// meeting-slots.ts).
//
// The dispatcher renders this while `status === 'capturing'` and while
// `status === 'processing'` with `stage === 'sealing'`.
//   capturing: the clock and Stop when this device records; "Recording on
//     another device" otherwise (Stop and save still works from here: the
//     recording device drains and ends its take); Discard with a confirm;
//     the live text (this device's preview, or the server's provisional live
//     rows); and, for PT-008 (addendum A4), "Ask about this meeting", which
//     opens the chat beside the canvas with the meeting attached, so the
//     composer stays usable while the meeting records.
//   after Stop (on this device or another one) and while sealing: the
//     finishing view (PL-30, meeting-finishing.tsx), which keeps the live
//     text on screen under "Finishing up" with this device's remaining upload.
// Right after Stop the canvas follows the server (sealing, then processing),
// which fixes develop2's stale page. Owner: WP-4.
import { useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsLiveCopy from '@/i18n/ns/meetings-live';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import { useWorkspace } from './context';
import { ResumeRecordingButton } from './meeting-audio-resume';
import { announceMeetingUpdated } from './meeting-events';
import { MeetingFinishingView, useMeetingStopped } from './meeting-finishing';
import { MeetingLivePreview, MeetingLiveRows } from './meeting-live';
import { RecordingButton, RecorderError } from './meeting-record-control';
import { formatClock } from './meeting-record-parts';
import { MeetingSourceLevels } from './meeting-source-levels';
import { meetingRecorder, recorderElapsedMs, useMeetingRecorder } from './meeting-recorder';
import type { MeetingCaptureViewProps } from './meeting-slots';
import { meetingsApi } from './meetings-api';
import { emptyTurnContext, type CompanyMeeting } from './types';

/** PT-008: keep asking Chat about the meeting while it records. */
export function MeetingAskPanel({ meeting }: { meeting: CompanyMeeting }) {
  const ws = useWorkspace();
  const live = useNamespaceCopy(meetingsLiveCopy);
  const attached = ws.turnContext.meetingIds?.includes(meeting.id) ?? false;
  const ask = () =>
    ws.send('', { ...emptyTurnContext(), meetingIds: [meeting.id] }, false, {
      kind: 'meeting',
      id: meeting.id,
    });
  return (
    <section className="ws-live-ask">
      {attached ? (
        <p className="ws-live-attached">
          {formatCopy(live.attachedMeeting, { title: meeting.title })}
        </p>
      ) : (
        <button className="ws-button" onClick={ask}>
          {live.askAboutMeeting}
        </button>
      )}
      <p className="ws-page-note">{live.askPlaceholder}</p>
    </section>
  );
}

function DiscardAction({ meeting, here }: { meeting: CompanyMeeting; here: boolean }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const discard = async () => {
    setBusy(true);
    try {
      if (here) await meetingRecorder.discard();
      else await meetingsApi.discardCapture(meeting.id);
      announceMeetingUpdated();
      ws.openCanvas(null);
    } catch {
      ws.notify(copy.captureClosed);
    } finally {
      setBusy(false);
    }
  };
  if (!confirming)
    return (
      <button className="ws-text-button" onClick={() => setConfirming(true)}>
        {copy.discardRecording}
      </button>
    );
  return (
    <span className="ws-record-confirm">
      <button className="ws-button danger" disabled={busy} onClick={() => void discard()}>
        {copy.discardRecording}
      </button>
      <button className="ws-button" disabled={busy} onClick={() => setConfirming(false)}>
        {copy.cancel}
      </button>
    </span>
  );
}

function StopElsewhere({ meeting, onChanged }: MeetingCaptureViewProps) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const [busy, setBusy] = useState(false);
  const stop = async () => {
    setBusy(true);
    try {
      await meetingsApi.stopCapture(meeting.id);
      announceMeetingUpdated();
      onChanged();
    } catch {
      ws.notify(copy.captureClosed);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="ws-button primary" disabled={busy} onClick={() => void stop()}>
      {copy.stopHere}
    </button>
  );
}

function CapturingActions({
  meeting,
  onChanged,
  here,
}: MeetingCaptureViewProps & { here: boolean }) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const phase = useMeetingRecorder((state) => state.phase);
  if (here && (phase === 'recovering' || phase === 'interrupted'))
    return (
      <div className="ws-actions">
        <ResumeRecordingButton />
        <button className="ws-button" onClick={() => void meetingRecorder.stopRecovered()}>
          {copy.stopHere}
        </button>
        <DiscardAction meeting={meeting} here={here} />
      </div>
    );
  // After Stop (here or elsewhere) the finishing view replaces this one.
  return (
    <div className="ws-actions">
      {here && phase === 'recording' ? (
        <RecordingButton />
      ) : (
        <StopElsewhere meeting={meeting} onChanged={onChanged} />
      )}
      <DiscardAction meeting={meeting} here={here} />
    </div>
  );
}

function CaptureStatus({ meeting, here }: { meeting: CompanyMeeting; here: boolean }) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const phase = useMeetingRecorder((state) => state.phase);
  const elapsed = useMeetingRecorder(recorderElapsedMs);
  if (here && phase === 'recording')
    return (
      <>
        <p className="ws-record-clock">
          {formatCopy(copy.elapsed, { time: formatClock(elapsed) })}
        </p>
        <MeetingSourceLevels />
      </>
    );
  if (here && (phase === 'recovering' || phase === 'interrupted'))
    return <p>{copy.recoveryBody}</p>;
  return (
    <p>
      {copy.recordingElsewhere} {meeting.capture ? formatClock(meeting.capture.elapsedMs) : ''}
    </p>
  );
}

export function MeetingCaptureView({
  meeting,
  onChanged,
  memberNotes,
  navigation,
  showTranscript = true,
  panel,
}: MeetingCaptureViewProps) {
  const live = useNamespaceCopy(meetingsLiveCopy);
  const phase = useMeetingRecorder((state) => state.phase);
  const { here, stopped } = useMeetingStopped(meeting);
  const previewHere = here && phase === 'recording';
  if (meeting.status !== 'capturing' || stopped)
    return (
      <MeetingFinishingView
        meeting={meeting}
        memberNotes={memberNotes}
        navigation={navigation}
        showTranscript={showTranscript}
        panel={panel}
      />
    );
  return (
    <div className="ws-canvas-body ws-meeting-capture" data-stage="capturing">
      <div className="ws-eyebrow">
        <span className="ws-record-dot live" aria-hidden="true" /> {live.stillRecording}
      </div>
      <h1>{meeting.title}</h1>
      {navigation}
      <div {...panel}>
        <CaptureStatus meeting={meeting} here={here} />
        {meeting.canEdit !== false && <RecorderError />}
        {meeting.canEdit !== false && (
          <CapturingActions meeting={meeting} onChanged={onChanged} here={here} />
        )}
        {memberNotes}
        <MeetingAskPanel meeting={meeting} />
        {showTranscript &&
          (previewHere ? (
            <MeetingLivePreview meeting={meeting} />
          ) : (
            <MeetingLiveRows meeting={meeting} />
          ))}
      </div>
    </div>
  );
}
